import { useEffect, useState } from "react";
import type { AxiosInstance } from "axios";
import { isRequestCanceled } from "@/services/apiErrors";
import { fetchBillingAccount } from "@/services/billingApi";
import type { BillingAccount } from "@/types/billing";

// How often a shown page reads the balance again: as often as the billing page
// does, so a credited top up reaches the header together with the page.
export const BILLING_ACCOUNT_REFRESH_MS = 5000;

interface UseBillingAccountOptions {
  enabled: boolean;
  /**
   * The project the caller is working in. The balance belongs to the
   * organization behind it, so switching to a project of another organization
   * is a different balance and reads it again.
   */
  projectId?: string | null;
  apiOverride?: AxiosInstance;
}

interface UseBillingAccountResult {
  account: BillingAccount | null;
  loading: boolean;
  error: unknown;
}

export function useBillingAccount({
  enabled,
  projectId,
  apiOverride,
}: UseBillingAccountOptions): UseBillingAccountResult {
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!enabled) {
      setAccount(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    let loaded = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // The latest read. An answer to an earlier one -- overtaken by the tab
    // being shown again -- is dropped rather than drawn over a newer figure.
    let latest = 0;

    const clearTimer = () => {
      if (timer === null) return;

      clearTimeout(timer);
      timer = null;
    };

    // The balance moves when the provider reports a payment, which is after
    // the page was drawn, so a shown page keeps reading it. A hidden one has
    // nobody to show it to and waits until it is shown again.
    const scheduleNext = () => {
      clearTimer();
      if (cancelled || document.visibilityState !== "visible") return;

      timer = setTimeout(() => {
        timer = null;
        void read();
      }, BILLING_ACCOUNT_REFRESH_MS);
    };

    const read = async () => {
      clearTimer();
      latest += 1;
      const current = latest;

      try {
        const next = await fetchBillingAccount({}, apiOverride);
        if (cancelled || current !== latest) return;
        loaded = true;
        setAccount(next);
        setError(null);
      } catch (caught) {
        if (cancelled || current !== latest || isRequestCanceled(caught)) {
          return;
        }
        // A caller whose role does not carry the billing permission reads no
        // balance, which is a header without a figure rather than an error the
        // visitor can do anything about. A later read that fails keeps the
        // figure already shown: one lost request is not a balance of nothing.
        if (!loaded) setAccount(null);
        setError(caught);
      } finally {
        if (!cancelled && current === latest) {
          setIsLoading(false);
          scheduleNext();
        }
      }
    };

    // Coming back to the tab -- from the provider's page, say -- is when the
    // balance has most likely moved, so it is read at once.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void read();
      } else {
        clearTimer();
      }
    };

    setIsLoading(true);
    void read();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, projectId, apiOverride]);

  return { account, loading: isLoading, error };
}
